import React, { useRef, useState } from 'react';
import ToggleSwitch from '../../components/ToggleSwitch';
import ChangeRoleConfirmationModal from './ChangeRoleConfirmationModal';
import ChangeRolePopup from './ChangeRolePopup';

// const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
// const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;

interface MemberProfileProps {
  member: {
    name: string;
    email: string;
    role: string;
    addedDate: string;
    invitedBy: string;
    lastLogin: string;
    status: string;
    avatar?: string;
  };
  onRoleChange?: (newRole: string) => void;
  onToggleDisable?: (checked: boolean) => void;
  onRemoveUsermodal?: () => void;
  isDisabled?: boolean;
  ownerRole?: string;
}

const MemberProfile: React.FC<MemberProfileProps> = ({
  member,
  onRoleChange,
  onToggleDisable,
  onRemoveUsermodal,
  isDisabled = false,
  ownerRole,
}) => {
  const savedProfileData = localStorage.getItem('profileData');
  const [showRolePopup, setShowRolePopup] = useState(false);
  const [confirmRole, setConfirmRole] = useState<string | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [currentRole, setCurrentRole] = useState(member.role);

  // Syncs currentRole with member prop changes
  React.useEffect(() => {
    setCurrentRole(member.role);
  }, [member.role]);

  // Toggles the role selection popup visibility
  // const toggleRolePopup = () => {
  //   setShowRolePopup((prev) => !prev);
  // };

  // Handles role change confirmation
  const handleRoleChangeConfirm = (newRole: string) => {
    onRoleChange?.(newRole);
    setCurrentRole(newRole);
    setConfirmRole(null);
  };

  // Handles role change request from popup
  const handleRequestConfirmation = (newRole: string) => {
    setConfirmRole(newRole);
    setShowRolePopup(false);
  };

  // Triggers the remove user modal
  const handleRemoveUser = () => {
    onRemoveUsermodal?.();
  };

  // Handles toggle switch change for enabling/disabling user
  const handleToggleDisable = (val: boolean) => {
    onToggleDisable?.(val);
  };

  return (
    <div className='bg-white rounded-lg border border-gray-200 shadow-sm p-6'>
      {/* Top row */}
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-xl font-bold text-teal-700'>
            {member.name[0]}
          </div>

          <div>
            <h3 className='text-[18px] font-semibold text-[#3F5265]'>
              {member.name}
            </h3>

            <p className='text-[#657584] text-[14px] relative'>
              <span className='mr-2'>{member.email}</span> |{' '}
              <span className='ml-2 mr-2'>{currentRole}</span>
              {/* {ownerRole === 'Owner' && (
                <button
                  onClick={toggleRolePopup}
                  className='inline-flex items-center text-teal-600 hover:underline ml-1'
                >
                  Change role{' '}
                  {showRolePopup ? (
                    <FiChevronDownComponent className='ml-1' />
                  ) : (
                    <FiChevronUpComponent className='ml-1' />
                  )}
                </button>
              )} */}
              {showRolePopup && (
                <div ref={popupRef} className='absolute left-40 top-5'>
                  <ChangeRolePopup
                    currentRole={currentRole}
                    onClose={() => setShowRolePopup(false)}
                    onRequestConfirmation={handleRequestConfirmation}
                  />
                </div>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        {ownerRole === 'Owner' && (
          <div className='flex items-center gap-5'>
            {/* <div className='flex items-center gap-2 text-sm text-gray-600'>
              <span>Temporarily Disable</span>
              <ToggleSwitch
                checked={isDisabled}
                onChange={handleToggleDisable}
              />
            </div> */}

            {/* <button
              onClick={handleRemoveUser}
              className='px-4 py-2 rounded border border-red-400 text-red-500 bg-white hover:bg-red-50'
            >
              Remove User
            </button> */}
          </div>
        )}
      </div>

      {/* Disabled warning */}
      {isDisabled && (
        <div className='mt-6 bg-red-50 border border-red-200 text-red-700 rounded-md p-4'>
          <p className='font-semibold'>
            This user’s access has been revoked. They can no longer log in or
            use the product.
          </p>
          <p className='text-sm mt-1'>
            You can restore access or remove the user permanently.
          </p>
        </div>
      )}

      {/* Meta info */}
      <div className='grid grid-cols-5 gap-4 mt-6 text-center items-center'>
        <span className='flex items-center mr-2 border-r-2 border-[grey]'>
          <p className='text-xs text-gray-500'>JOINED DATE</p>
          <p className='font-normal ml-4'>{member.addedDate || '-'}</p>
        </span>
        <span className='flex items-center ml-2 mr-2 border-r-2 border-[grey]'>
          <p className='text-xs text-gray-500'>INVITED BY</p>
          <p className='font-normal ml-4'>
            {savedProfileData ? JSON.parse(savedProfileData).firstName : ''}{' '}
            {savedProfileData ? JSON.parse(savedProfileData).lastName : ''}
          </p>
        </span>
        <span className='flex items-center ml-2 mr-2 border-r-2 border-[grey]'>
          <p className='text-xs text-gray-500'>LAST LOGIN</p>
          <p className='font-normal ml-4'>{member.lastLogin || '-'}</p>
        </span>
        <span className='flex items-center ml-2 mr-2'>
          <p className='text-xs text-gray-500'>STATUS</p>
          <span
            className={`px-2 py-1 text-xs rounded-full ml-4 ${member.status === 'Active'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-200 text-gray-600'
              }`}
          >
            {member.status}
          </span>
        </span>
      </div>

      {/* Confirmation Modal */}
      {confirmRole && (
        <ChangeRoleConfirmationModal
          memberName={member.name}
          oldRole={currentRole}
          newRole={confirmRole}
          onClose={() => setConfirmRole(null)}
          onConfirm={() => handleRoleChangeConfirm(confirmRole)}
        />
      )}
    </div>
  );
};

export default MemberProfile;
